import React, { Suspense, lazy, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Navigation } from '@/components/Navigation';
import { LimitedSpots } from '@/components/LimitedSpots';
import { LogoStrip } from '@/components/LogoStrip';
import { SimpleReveal } from '@/components/SimpleReveal';
import { ScrollController } from '@/components/ScrollController';
import { ProductPreviewCarousel } from '@/components/ProductPreviewCarousel';
import { ScrollProgress } from '@/components/ScrollProgress';
import { SequentialText } from '@/components/SequentialText';
import { CountingMetric } from '@/components/CountingMetric';
import { AudioWaveform } from '@/components/AudioWaveform';
// Note: ParticleBackground available at @/components/ParticleBackground if needed
import { TypedWord } from '@/components/TypedWord';
import { IntroducingBar } from '@/components/IntroducingBar';
import { RealIssueSection } from '@/components/WelcomeSection';
import { DarkChapter } from '@/components/layout/DarkChapter';
import { ChevronDown, ArrowRight, Volume2, Play } from 'lucide-react';
import { ArtMeetsScienceFormula } from '@/components/ArtMeetsScienceFormula';
import { FounderMessageSection } from '@/components/FounderMessageSection';
import { VideoTestimonialsCarousel } from '@/components/VideoTestimonialsCarousel';
import BeforeAfterCTA from '@/components/BeforeAfterCTA';
import { trackLead } from '@/lib/metaPixel';

// Helper to retry failed chunk loads
const lazyWithRetry = <T extends React.ComponentType<any>,>(importer: () => Promise<{
  default: T;
}>) => lazy(async () => {
  try {
    return await importer();
  } catch (err) {
    const message = String(err);
    const looksLikeChunkError = /ChunkLoadError|Loading chunk|Importing a module script failed|Failed to fetch dynamically imported module/.test(message);
    if (looksLikeChunkError) {
      const retries = Number(sessionStorage.getItem('lazy-retry') || '0');
      if (retries < 1) {
        sessionStorage.setItem('lazy-retry', String(retries + 1));
        const url = new URL(window.location.href);
        url.searchParams.set('v', String(Date.now()));
        window.location.replace(url.toString());
      }
    }
    throw err;
  }
});

// Lazy load below-fold components for better performance
const DeliverablesTimeline = lazyWithRetry(() => import('@/components/DeliverablesTimeline'));
const CaseStudiesFAQ = lazyWithRetry(() => import('@/components/CaseStudiesFAQ'));
const FounderNote = lazyWithRetry(() => import('@/components/FounderNote'));
const WhoThisIsFor = lazyWithRetry(() => import('@/components/WhoThisIsFor'));
const DetailedCaseStudyTabs = lazyWithRetry(() => import('@/components/DetailedCaseStudyTabs'));
const TestimonialsSection = lazyWithRetry(() => import('@/components/TestimonialsSection'));
const Footer = lazyWithRetry(() => import('@/components/Footer'));

// Hero Video Component with tap-for-sound functionality and Apply button
function HeroVideo({
  applyUrl
}: {
  applyUrl: string;
}) {
  const [isVideoMuted, setIsVideoMuted] = useState(true);
  return <div className="w-full max-w-3xl mx-auto">
      <SimpleReveal delay={240}>
        {/* Container for ambient glow effect */}
        <div className="relative">
          {/* Static noise particles - subtle texture */}
          <div className="absolute -inset-24 pointer-events-none opacity-[0.08]" style={{
          backgroundImage: 'radial-gradient(circle at center, rgba(255,255,255,0.15) 1px, transparent 1px)',
          backgroundSize: '24px 24px',
          maskImage: 'radial-gradient(ellipse at center, black 30%, transparent 80%)',
          WebkitMaskImage: 'radial-gradient(ellipse at center, black 30%, transparent 80%)'
        }} />
          
          {/* Ambient light glow behind video */}
          <div className="absolute -inset-20 rounded-3xl blur-3xl pointer-events-none" style={{
          background: 'radial-gradient(ellipse at center, rgba(0, 113, 227, 0.4) 0%, rgba(0, 113, 227, 0.2) 50%, transparent 85%)'
        }} />
          
          <div className="relative rounded-2xl overflow-hidden shadow-[0_8px_40px_rgba(0,0,0,0.6)] border border-white/10 transition-all duration-500 hover:shadow-[0_12px_50px_rgba(0,0,0,0.7)] hover:-translate-y-1">
          <div style={{
            padding: '56.25% 0 0 0',
            position: 'relative'
          }}>
            {isVideoMuted ? <iframe src="https://player.vimeo.com/video/1152297136?background=1&autoplay=1&loop=1&muted=1" frameBorder="0" allow="autoplay; fullscreen; picture-in-picture; clipboard-write" style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%'
            }} title="DK Artists" /> : <iframe src="https://player.vimeo.com/video/1152297136?autoplay=1&badge=0&autopause=0&player_id=0&app_id=58479" frameBorder="0" allow="autoplay; fullscreen; picture-in-picture; clipboard-write" style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%'
            }} title="DK Artists" />}
          </div>
          
          {/* Gradient overlay (only in ambient mode) */}
          {isVideoMuted && <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent pointer-events-none" />}
          
          {/* Play button overlay (only in ambient mode) */}
          {isVideoMuted && <button onClick={() => setIsVideoMuted(false)} className="absolute inset-0 flex items-center justify-center cursor-pointer group">
              <div className="w-16 h-16 rounded-full bg-white/20 backdrop-blur-sm border border-white/30 flex items-center justify-center transition-all duration-300 group-hover:bg-white/30 group-hover:scale-110">
                <Play className="w-7 h-7 text-white ml-1" fill="white" fillOpacity={0.9} />
              </div>
            </button>}
          
          {/* Tap for Sound badge (top right, only in ambient mode) */}
          {isVideoMuted && <div className="absolute top-3 right-3 flex items-center gap-1.5 bg-black/60 backdrop-blur-sm px-3 py-1.5 rounded-full border border-white/20 pointer-events-none">
              <Volume2 className="w-3.5 h-3.5 text-white/80" />
              <span className="text-white/80 text-xs font-medium">Tap for sound</span>
            </div>}
          </div>
        </div>
        
        {/* Apply Button - Below Video */}
        <div className="mt-8 flex justify-center">
          <div className="inline-block group relative rounded-xl rotating-border-button">
            <Button variant="hero-blue-ring" size="hero" asChild>
              <a href={applyUrl} target="_blank" rel="noopener noreferrer" onClick={trackLead}>
                <span className="inline-flex items-center gap-2">
                  Apply Now
                  <ArrowRight className="w-4 h-4 arrow-slide" />
                </span>
              </a>
            </Button>
          </div>
        </div>
      </SimpleReveal>
    </div>;
}
export default function ArtistEngine() {
  const applyUrl = "https://a23j5fry30t.typeform.com/to/CzjaFT5z";
  const whyNowPhrases = ["Algorithms reward consistent signals", "Artists with systems grow faster than those without", "Your music deserves a structure that compounds"];
  return <>
      <ScrollProgress />
      <ScrollController>
        <div className="min-h-screen text-text">
          <Navigation />
          
          {/* Unified Dark Chapter: Hero + Video Section */}
          <DarkChapter>
            {/* Hero Section */}
            <section id="hero" data-nav-theme="dark" className="relative flex flex-col items-center overflow-visible pt-24 lg:pt-36 pb-8 lg:pb-16">
              {/* Main Content - Centered Stack */}
              <div className="w-full max-w-5xl mx-auto px-6 lg:px-12">
                
                {/* Badge with Audio Waveform */}
                <SimpleReveal delay={60}>
                  <div className="mb-4 text-center flex flex-col items-center">
                    <AudioWaveform />
                    <div className="inline-flex items-center bg-white/5 border border-white/10 rounded-md px-2 lg:px-2.5 py-1 lg:py-1.5 gap-2">
                      <span className="font-mono text-[10px] lg:text-xs text-white/50 tracking-[0.15em] uppercase">
                        &gt; THE ARTIST ENGINE
                      </span>
                    </div>
                  </div>
                </SimpleReveal>

                {/* Headline - Centered */}
                <SimpleReveal delay={120}>
                  <h1 className="font-headline font-bold text-center text-white mb-4 text-[clamp(2rem,5vw,3.5rem)] leading-[1.1] tracking-[-0.03em] max-w-4xl mx-auto">We Grow Your Music <span className="whitespace-nowrap">For You</span></h1>
                </SimpleReveal>
                
                {/* Subtext - Centered */}
                <SimpleReveal delay={180}>
                  <p className="text-sm lg:text-base text-white/70 text-center max-w-xl mx-auto mb-8 leading-[1.6]" style={{
                  letterSpacing: '0.3px'
                }}>
                    So much growth it becomes a better problem.
                  </p>
                </SimpleReveal>

                {/* Video - Full Width Centered */}
                <HeroVideo applyUrl={applyUrl} />
                
                {/* Trust line */}
                <SimpleReveal delay={420}>
                  <p className="text-xs text-white/50 text-center mt-8 font-medium tracking-wide">
                    Trusted by over 30,000 artists generating 100M+ streams.
                  </p>
                </SimpleReveal>
                
              </div>
            </section>

          </DarkChapter>

          {/* The Real Issue Section - White background starts here */}
          <div id="welcome" data-nav-theme="light">
            <RealIssueSection />
          </div>

          {/* Artist Growth Formula - Where Art Meets Science */}
        <div data-nav-theme="light">
          <ArtMeetsScienceFormula />
        </div>
        
        <div data-nav-theme="dark">
          <IntroducingBar />
          <FounderMessageSection />
        </div>

        {/* Arrow separator between Founder and Case Studies */}
        <div className="flex justify-center py-2 lg:py-4" style={{
          backgroundColor: 'hsl(0, 0%, 3%)'
        }}>
          <ChevronDown className="w-6 h-6 text-white/20" />
        </div>

        {/* Real Results Section - Apple-style blue grid */}
        <section id="artist-results" data-nav-theme="dark" className="section-results py-16 lg:py-32 relative" style={{
          background: 'radial-gradient(ellipse at center, hsl(0, 0%, 7%) 0%, hsl(0, 0%, 3%) 70%)'
        }}>
          {/* Unified white grid with radial mask - same as other sections */}
          <div className="absolute inset-0 grid-pattern" />
          
          {/* Film grain texture overlay - for consistency */}
          <div className="absolute inset-0 z-[1] opacity-[0.025] pointer-events-none" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
            mixBlendMode: 'overlay'
          }} />
          
          <div className="max-w-7xl mx-auto px-6 relative z-10">
            {/* Badge */}
            <SimpleReveal>
              <div className="flex justify-center mb-6">
                <div className="inline-flex items-center gap-2 px-2 py-1 md:px-3 md:py-1.5 bg-[#0071e3]/10 border border-[#0071e3]/25 rounded-md shadow-[0_0_20px_rgba(0,113,227,0.3)] md:shadow-none transition-all duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] hover:bg-[#0071e3]/15 hover:border-[#0071e3]/35 hover:shadow-[0_0_20px_rgba(0,113,227,0.3)]">
                  <span className="font-mono text-xs font-medium text-[#0071e3] tracking-wider md:tracking-[0.15em] uppercase">
                    &gt; CASE STUDIES
                  </span>
                </div>
              </div>
            </SimpleReveal>

            {/* Title */}
            <SimpleReveal className="text-center">
              <h2 className="headline-lg text-white w-fit mx-auto mb-2">
                Real Growth.<br />Real Results.{' '}
                <span className="inline-block bg-[#f5f5f7] text-[#1d1d1f] px-3 py-1 rounded-lg font-bold -rotate-2 shadow-[0_0_40px_rgba(0,113,227,0.7)]">
                  Real Fast
                </span>
              </h2>
            </SimpleReveal>
            
            {/* Subheading */}
            <SimpleReveal>
            <p className="body-large text-white/70 text-center max-w-2xl mx-auto mb-12">
              The playbook that compounds repeatable results.
            </p>
            </SimpleReveal>

            {/* Result-focused case study tabs */}
            <Suspense fallback={<div className="h-96 flex items-center justify-center"><div className="text-white/50">Loading...</div></div>}>
              <DetailedCaseStudyTabs />
            </Suspense>

            {/* Subtle link to more case studies */}
            <SimpleReveal delay={200}>
              <div className="flex justify-center mt-8">
                <Link to="/case-studies" className="inline-flex items-center gap-2 text-sm text-white/40 hover:text-white/70 transition-colors duration-300 group">
                  <span>View all case studies</span>
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
                </Link>
              </div>
            </SimpleReveal>
          </div>
        </section>

        {/* Video Testimonials Carousel - Between Case Studies and What Artists Say */}
        <section data-nav-theme="dark" className="relative" style={{
          background: 'radial-gradient(ellipse at center, hsl(0, 0%, 7%) 0%, hsl(0, 0%, 3%) 70%)'
        }}>
          {/* Unified white grid with radial mask */}
          <div className="absolute inset-0 grid-pattern" />
          
          {/* Film grain texture overlay */}
          <div className="absolute inset-0 z-[1] opacity-[0.025] pointer-events-none" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
            mixBlendMode: 'overlay'
          }} />
          
          <div className="relative z-10">
            <VideoTestimonialsCarousel />
          </div>
        </section>

        {/* Testimonials Section */}
        <div data-nav-theme="dark">
          <Suspense fallback={<div className="h-96 bg-[#0a0a0a] flex items-center justify-center"><div className="text-white/50">Loading...</div></div>}>
            <TestimonialsSection />
          </Suspense>
        </div>

        {/* FAQ Section */}
        <Suspense fallback={<div className="h-64 bg-gray-50" />}>
          <CaseStudiesFAQ />
        </Suspense>

        {/* Before/After CTA Section */}
        <div data-nav-theme="dark">
          <Suspense fallback={<div className="h-96 flex items-center justify-center"><div className="text-white/50">Loading...</div></div>}>
            <BeforeAfterCTA applyUrl={applyUrl} />
          </Suspense>
        </div>

          {/* Footer */}
          <Suspense fallback={<div className="h-20 bg-gray-900" />}>
            <Footer applyUrl={applyUrl} />
          </Suspense>
        </div>
      </ScrollController>
    </>;
}